<script setup>
// Tela de início de Atender (aprovada em 12/09/2026): o card da fila grande,
// com o número, a frase da fila, quatro indicadores e os botões; à direita,
// um celular com a fila real. Embaixo, os chips por filtro e por caixa, o
// gráfico de conversas por dia com os dois números-chave em evidência, e a
// linha com Chat da equipe, Relatório completo e Configuração.
import { computed, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { groupDestinations } from './hubWorlds';
import { workspaceV2Path } from 'dashboard/helper/workspaceV2';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAttendanceHome, EMPTY } from './useAttendanceHome';
import './hubDesign.css';

const props = defineProps({
  world: { type: Object, required: true },
});
const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const home = useAttendanceHome();

const destination = id =>
  props.world.destinations.find(candidate => candidate.id === id) || null;
const withQuery = (dest, query) =>
  dest ? { ...dest.to, query: { ...dest.to.query, ...query } } : null;
const conversations = computed(() => destination('conversations'));
const queueTo = filter => withQuery(conversations.value, { filter });
const inboxTo = inboxId =>
  accountScopedRoute('inbox_dashboard', { inbox_id: inboxId });

const go = to => {
  if (to) router.push(to);
};

// A tela genérica das áreas já tinha "Voltar"; Atender não tinha, e no
// celular a pessoa ficava sem caminho de volta ao início.
const goToHub = () => router.push(accountScopedRoute('focus_hub'));

const conversationTo = id =>
  workspaceV2Path(
    router.resolve(
      accountScopedRoute('inbox_conversation', { conversation_id: id })
    ).fullPath
  );

const queueTitle = computed(() =>
  t('HUB.ATTENDANCE.WAITING_TITLE', home.allCount.value, {
    count: home.allCount.value,
  })
);
const queueLine = computed(() => {
  const parts = [
    t('HUB.HOME.QUEUE_UNASSIGNED', home.unassignedCount.value, {
      count: home.unassignedCount.value,
    }),
    t('HUB.HOME.QUEUE_MINE', home.mineCount.value, {
      count: home.mineCount.value,
    }),
  ];
  const oldest = home.longestWaiting.value;
  if (oldest) {
    parts.push(
      t('HUB.ATTENDANCE.OLDEST', { wait: oldest.wait, name: oldest.name })
    );
  }
  return parts.join('. ') + '.';
});

// "Ver mais": tudo o que Atender tem, nos tópicos do mapa (Conversas, Equipe,
// Números, Configuração). Fica fechado para o botão rápido respirar.
const seeMore = ref(false);
const moreGroups = computed(() => groupDestinations(props.world.destinations));
const badgeFor = id => (id === 'internal_chat' ? home.teamChatUnread.value : 0);

// Os valores das barras só existiam no ::after (attr(data-v)): leitor de tela
// não lia nada. O gráfico vira uma imagem com a semana resumida no rótulo.
const chartAria = computed(() => {
  const days = home.chart.value?.days || [];
  const list = days
    .map(day =>
      // vue-i18n 9: nomeados no 2º argumento e plural no 3º. Na ordem
      // inversa o objeto vira opção de tradução e {day} sai vazio.
      t(
        'HUB.ATTENDANCE.CHART_DAY',
        { day: day.label, count: day.value },
        day.value
      )
    )
    .join(', ');
  return t('HUB.ATTENDANCE.CHART_ARIA', { days: list });
});
</script>

<template>
  <div
    class="w-full flex-1 bg-n-background px-5 py-7 text-n-slate-12 sm:px-6 md:px-10 md:py-9 [&_.hub-surface]:!border-n-weak [&_.hub-surface]:!bg-n-solid-1 [&_.hub-surface]:!shadow-none [&_.hub-surface]:before:!hidden [&_.hub-stat]:!border-n-weak [&_.hub-stat]:!bg-n-solid-1 [&_.hub-stat]:!shadow-none [&_.hub-chip]:!border-n-weak [&_.hub-chip]:!bg-n-solid-1 [&_.hub-chip]:!text-n-slate-11 [&_.hub-chip:hover]:!bg-n-alpha-1 [&_.hub-chip-count]:!bg-n-alpha-2 [&_.hub-chip-count]:!text-n-slate-12 [&_.hub-tile]:!border-n-weak [&_.hub-tile]:!bg-n-solid-1 [&_.hub-tile]:!shadow-none [&_.hub-tile-ic]:!bg-n-alpha-2 [&_.hub-tile-ic]:!text-n-slate-11 [&_.hub-tile-n]:!bg-n-blue-9 [&_.hub-tile-n]:!text-white [&_.hub-eyebrow-dot]:!bg-n-blue-9 [&_.hub-eyebrow-dot]:!shadow-none [&_.att-kpi--warn_b]:!text-n-slate-12 [&_.hub-delta]:!text-n-slate-11 [&_.att-bar]:!bg-n-blue-9 [&_.att-bar]:!bg-none [&_.att-bar--hi]:!bg-n-blue-11 [&_.att-title]:!text-n-slate-12 [&_.att-title_small]:!text-n-slate-11 [&_.att-kpis_span]:!text-n-slate-11 [&_.hub-stat-h]:!text-n-slate-11 [&_.hub-stat-l]:!text-n-slate-11 [&_.hub-tile-s]:!text-n-slate-11"
    data-testid="attendance-home"
  >
    <div class="mx-auto max-w-[1180px]">
      <button
        type="button"
        class="mb-4 inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-bold text-n-slate-11 transition-colors hover:bg-n-solid-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        data-testid="attendance-back"
        @click="goToHub"
      >
        <span class="i-ri-arrow-left-line size-4" aria-hidden="true" />
        {{ t('HUB.AREA_BACK') }}
      </button>
      <section
        class="hub-surface hub-surface--attendance att-hero"
        data-testid="attendance-hero"
      >
        <div class="att-copy">
          <div>
            <span class="hub-eyebrow">
              <span class="hub-eyebrow-dot" aria-hidden="true" />
              {{ t('HUB.ATTENDANCE.LIVE_QUEUE') }}
            </span>
            <h2 class="att-title">
              {{ queueTitle }}
              <small>{{ queueLine }}</small>
            </h2>
          </div>
          <div class="att-kpis">
            <div :class="{ 'att-kpi--warn': home.unassignedCount.value > 0 }">
              <b class="hub-num">{{ home.unassignedCount.value }}</b>
              <span>{{ t('HUB.ATTENDANCE.KPI_UNASSIGNED') }}</span>
            </div>
            <div>
              <b class="hub-num">{{ home.mineCount.value }}</b>
              <span>{{ t('HUB.ATTENDANCE.KPI_MINE') }}</span>
            </div>
            <div>
              <b class="hub-num">{{
                home.longestWaiting.value?.wait || EMPTY
              }}</b>
              <span>{{ t('HUB.STATS.MAX_WAIT') }}</span>
            </div>
            <div v-if="home.csat.value">
              <b class="hub-num">{{ home.csat.value }}</b>
              <span>{{ t('HUB.HOME.KPI_CSAT') }}</span>
            </div>
          </div>
          <div class="att-actions">
            <Button
              icon="i-ph-chat-circle"
              :label="t('HUB.OPEN_QUEUE')"
              data-testid="hub-primary-attendance"
              @click="go(conversations?.to)"
            />
            <Button
              variant="outline"
              class="!text-n-slate-12"
              color="slate"
              :label="t('HUB.HOME.VIEW_UNASSIGNED')"
              data-testid="attendance-pick-next"
              @click="go(queueTo('unassigned'))"
            />
            <Button
              variant="link"
              color="slate"
              class="!text-n-slate-11"
              :icon="seeMore ? 'i-ph-caret-up' : 'i-ph-caret-down'"
              :label="t('SEARCH.VIEW_MORE')"
              :aria-expanded="seeMore"
              aria-controls="attendance-more"
              data-testid="attendance-see-more"
              @click="seeMore = !seeMore"
            />
          </div>
        </div>
        <div class="flex items-end justify-center p-5 pb-0">
          <div
            class="flex h-[350px] w-full max-w-[300px] flex-col overflow-hidden rounded-t-3xl border border-b-0 border-n-weak bg-n-solid-1 text-n-slate-12"
            data-testid="attendance-phone"
          >
            <div
              class="mx-auto my-3 h-2 w-16 rounded-full bg-n-alpha-2"
              aria-hidden="true"
            />
            <h3 class="border-b border-n-weak px-4 pb-3 text-sm font-semibold">
              {{ t('HUB.ATTENDANCE.RECENT_TITLE') }}
            </h3>
            <div class="flex-1 overflow-y-auto">
              <RouterLink
                v-for="row in home.recentRows.value"
                :key="row.id"
                :to="conversationTo(row.id)"
                class="flex min-h-14 items-center gap-2 border-b border-n-weak px-3 py-3 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
              >
                <img
                  v-if="row.avatar"
                  :src="row.avatar"
                  alt=""
                  class="size-8 shrink-0 rounded-full object-cover"
                />
                <span
                  v-else
                  class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs font-medium"
                >
                  {{ row.initials }}
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-xs font-semibold">{{
                    row.name
                  }}</span>
                  <span class="block truncate text-xs text-n-slate-11">{{
                    row.text
                  }}</span>
                </span>
                <span class="flex shrink-0 flex-col items-end gap-1">
                  <span class="text-[10px] text-n-slate-11">{{
                    row.time
                  }}</span>
                  <span
                    v-if="row.unread"
                    class="rounded-full bg-n-blue-9 px-1.5 text-[10px] text-white"
                  >
                    {{ row.unread }}
                  </span>
                </span>
              </RouterLink>
              <div
                v-if="home.queueError.value"
                class="p-4 text-sm text-n-slate-11"
                role="status"
              >
                {{ t('HUB.ATTENDANCE.RECENT_ERROR') }}
                <Button
                  class="mt-2"
                  variant="outline"
                  color="slate"
                  :label="t('HUB.ATTENDANCE.RETRY')"
                  @click="home.reloadQueue()"
                />
              </div>
              <p
                v-else-if="!home.queueLoaded.value"
                class="p-4 text-sm text-n-slate-11"
                role="status"
              >
                {{ t('HUB.ATTENDANCE.RECENT_LOADING') }}
              </p>
              <p
                v-else-if="!home.recentRows.value.length"
                class="p-4 text-sm text-n-slate-11"
              >
                {{ t('HUB.ATTENDANCE.QUEUE_EMPTY') }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="seeMore"
        id="attendance-more"
        class="mt-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        data-testid="attendance-more"
      >
        <h3 class="m-0 text-xs font-semibold text-n-slate-11">
          {{ t('HUB.HOME.QUEUE_BY') }}
        </h3>
        <div class="mt-2 flex flex-wrap gap-2" data-testid="attendance-chips">
          <button type="button" class="hub-chip" @click="go(queueTo('mine'))">
            {{ t('HUB.HOME.CHIP_MINE') }}
            <span class="hub-chip-count">{{ home.mineCount.value }}</span>
          </button>
          <button
            type="button"
            class="hub-chip"
            @click="go(queueTo('unassigned'))"
          >
            {{ t('HUB.HOME.CHIP_UNASSIGNED') }}
            <span class="hub-chip-count">{{ home.unassignedCount.value }}</span>
          </button>
          <button type="button" class="hub-chip" @click="go(queueTo('all'))">
            {{ t('HUB.HOME.CHIP_ALL') }}
          </button>
        </div>

        <template v-if="home.inboxChips.value.length">
          <h3 class="m-0 mt-4 text-xs font-semibold text-n-slate-11">
            {{ t('HUB.HOME.INBOXES') }}
          </h3>
          <div class="mt-2 flex flex-wrap gap-2" data-testid="attendance-boxes">
            <button
              v-for="inbox in home.inboxChips.value"
              :key="inbox.id"
              type="button"
              class="hub-chip"
              @click="go(inboxTo(inbox.id))"
            >
              <span class="size-4" :class="[inbox.icon]" aria-hidden="true" />
              {{ inbox.label }}
            </button>
          </div>
        </template>

        <div
          v-for="group in moreGroups"
          :key="group.id"
          data-testid="attendance-more-group"
        >
          <h3 class="m-0 mt-4 text-xs font-semibold text-n-slate-11">
            {{ t(group.labelKey) }}
          </h3>
          <div class="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <button
              v-for="item in group.destinations"
              :key="item.id"
              type="button"
              class="hub-tile"
              :data-testid="`hub-dest-${item.id}`"
              @click="go(item.to)"
            >
              <span class="hub-tile-ic">
                <span class="size-5" :class="[item.icon]" aria-hidden="true" />
              </span>
              <span class="min-w-0">
                <span class="hub-tile-t">{{
                  item.label || t(item.labelKey)
                }}</span>
                <span class="hub-tile-s">{{ t(item.subtitleKey) }}</span>
              </span>
              <span v-if="badgeFor(item.id)" class="hub-tile-n">
                <span aria-hidden="true">{{ badgeFor(item.id) }}</span>
                <span class="sr-only">{{
                  t('HUB.ATTENDANCE.UNREAD', badgeFor(item.id), {
                    count: badgeFor(item.id),
                  })
                }}</span>
              </span>
            </button>
          </div>
        </div>
      </section>

      <div class="mt-4 grid gap-3 lg:grid-cols-3" data-testid="attendance-week">
        <!-- Relatório negado (atendente): o bloco ocupa a linha inteira com o
             aviso, e os números da semana somem em vez de mostrar "·". -->
        <div
          class="hub-stat"
          :class="
            home.seriesForbidden.value ? 'lg:col-span-3' : 'lg:col-span-2'
          "
        >
          <div class="flex items-baseline justify-between gap-3">
            <b class="text-sm">{{ t('HUB.ATTENDANCE.PER_DAY') }}</b>
            <span class="att-muted text-xs">{{
              t('HUB.ATTENDANCE.LAST_7')
            }}</span>
          </div>
          <div
            v-if="home.chart.value"
            class="att-bars"
            role="img"
            :aria-label="chartAria"
            data-testid="attendance-chart"
          >
            <div
              v-for="(day, index) in home.chart.value.days"
              :key="day.key"
              class="att-bar"
              :class="{
                'att-bar--hi': index === home.chart.value.days.length - 1,
              }"
              :style="{ height: `${Math.max(day.height, 4)}%` }"
              :data-v="day.value"
            />
          </div>
          <div v-if="home.chart.value" class="att-days" aria-hidden="true">
            <span v-for="day in home.chart.value.days" :key="`l-${day.key}`">{{
              day.label
            }}</span>
          </div>
          <p
            v-if="home.seriesForbidden.value"
            class="att-muted mt-3 text-xs"
            data-testid="attendance-week-forbidden"
          >
            {{ t('HUB.HOME.NOT_FOR_ROLE') }}
          </p>
        </div>
        <div
          v-if="!home.seriesForbidden.value"
          class="grid gap-3"
          data-testid="attendance-week-stats"
        >
          <div class="hub-stat">
            <div class="hub-stat-l">{{ t('HUB.ATTENDANCE.WEEK_TOTAL') }}</div>
            <div class="hub-stat-v">
              {{ home.chart.value ? home.chart.value.weekTotal : EMPTY }}
              <span
                v-if="home.chart.value?.weekDelta"
                class="hub-delta"
                :class="{
                  'hub-delta--down':
                    home.chart.value.weekTotal < home.chart.value.previousTotal,
                }"
              >
                {{ home.chart.value.weekDelta }}
              </span>
            </div>
            <div class="hub-stat-h">
              {{
                home.chart.value
                  ? t('HUB.ATTENDANCE.WEEK_PREVIOUS', {
                      count: home.chart.value.previousTotal,
                    })
                  : ''
              }}
            </div>
          </div>
          <div class="hub-stat">
            <div class="hub-stat-l">
              {{ t('HUB.ATTENDANCE.FIRST_RESPONSE') }}
            </div>
            <div class="hub-stat-v">
              {{ home.firstResponse.value.label }}
              <span
                v-if="home.firstResponse.value.delta"
                class="hub-delta"
                :class="{ 'hub-delta--down': home.firstResponse.value.slower }"
              >
                {{ home.firstResponse.value.delta }}
              </span>
            </div>
            <div class="hub-stat-h">
              {{ t('HUB.ATTENDANCE.FIRST_RESPONSE_HINT') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.att-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  min-height: 330px;
}
.att-copy {
  padding: 30px 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
}
.att-title {
  margin: 10px 0 0;
  font-size: 44px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.02;
}
.att-title small {
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: var(--hv-fg2);
  letter-spacing: 0;
  margin-top: 10px;
  line-height: 1.4;
  max-width: 44ch;
}
.att-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 28px;
}
.att-kpis b {
  display: block;
  font-size: 30px;
}
.att-kpis span {
  font-size: 12px;
  color: var(--hv-fg2);
}
.att-kpi--warn b {
  color: var(--hv-amber);
}
.att-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.att-bars {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  align-items: end;
  height: 120px;
  margin-top: 26px;
}
.att-bar {
  position: relative;
  border-radius: 6px 6px 3px 3px;
  background: linear-gradient(
    180deg,
    rgba(124, 108, 255, 0.9),
    rgba(124, 108, 255, 0.35)
  );
}
.att-bar--hi {
  background: linear-gradient(180deg, var(--hv-fg), rgba(128, 128, 128, 0.55));
}
.att-bar::after {
  content: attr(data-v);
  position: absolute;
  top: -20px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  color: var(--hv-fg);
  font-variant-numeric: tabular-nums;
}
.att-muted {
  color: var(--hv-fg3);
}
.att-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  margin-top: 8px;
  font-size: 11px;
  color: var(--hv-fg3);
  text-align: center;
  font-weight: 600;
  text-transform: lowercase;
}
@media (max-width: 860px) {
  .att-hero {
    grid-template-columns: 1fr;
  }
  .att-title {
    font-size: 34px;
  }
}
</style>
